import React, { useEffect, useState } from 'react';
import { ToastMessage, ToastSeverity } from '@stores/toastStore';
import SvgCheck from '@components/icons/Check';
import SvgExclamationCircle from '@components/icons/ExclamationCircle';
import SvgExclamationTriangle from '@components/icons/ExclamationTriangle';
import SvgInfoCircle from '@components/icons/InfoCircle';
import SvgClose from '@components/icons/Close';

interface CustomToastProps {
  toast: ToastMessage;
  onClose: () => void;
}

const CustomToast: React.FC<CustomToastProps> = ({ toast, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Reset animací při změně toastu
    setIsVisible(false);
    setIsLeaving(false);
    
    // Animace vstupu
    const enterTimer = setTimeout(() => setIsVisible(true), 10);
    
    // Automatické zavření
    const closeTimer = setTimeout(() => {
      handleClose();
    }, toast.life || 5000);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(closeTimer);
    };
  }, [toast.id, toast.life]); // Přidáno toast.id pro reakci na změnu toastu

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose();
    }, 300); // Doba animace výstupu
  };

  const getSeverityStyles = (severity: ToastSeverity) => {
    switch (severity) {
      case 'success':
        return {
          bg: 'bg-green-50 border-green-200',
          iconColor: 'text-green-500',
          text: 'text-green-800',
          Icon: SvgCheck
        };
      case 'error':
        return {
          bg: 'bg-red-50 border-red-200',
          iconColor: 'text-red-500',
          text: 'text-red-800',
          Icon: SvgExclamationCircle
        };
      case 'warn':
        return {
          bg: 'bg-yellow-50 border-yellow-200',
          iconColor: 'text-yellow-500',
          text: 'text-yellow-800',
          Icon: SvgExclamationTriangle
        };
      case 'info':
        return {
          bg: 'bg-blue-50 border-blue-200',
          iconColor: 'text-blue-500',
          text: 'text-blue-800',
          Icon: SvgInfoCircle
        };
      default:
        return {
          bg: 'bg-gray-50 border-gray-200',
          iconColor: 'text-gray-500',
          text: 'text-gray-800',
          Icon: SvgInfoCircle
        };
    }
  };

  const styles = getSeverityStyles(toast.severity);

  return (
    <div
      className={`
        fixed z-[9999] max-w-sm w-full
        top-4 left-1/2 -translate-x-1/2 md:left-auto md:right-4 md:translate-x-0
        transform transition-all duration-300 ease-in-out
        ${isVisible && !isLeaving 
          ? 'translate-y-0 opacity-100 scale-100' 
          : 'translate-y-[-100%] opacity-0 scale-95'
        }
      `}
    >
      <div
        className={`
          ${styles.bg} border rounded-lg shadow-lg p-4
          flex items-start gap-3
        `}
      >
        <div className="flex-shrink-0">
          <styles.Icon className={`${styles.iconColor} text-lg`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className={`font-medium text-sm ${styles.text}`}>
            {toast.summary}
          </div>
          {toast.detail && (
            <div className={`text-sm mt-1 ${styles.text} opacity-90`}>
              {toast.detail}
            </div>
          )}
        </div>
        
        <button
          onClick={handleClose}
          className={`
            flex-shrink-0 ${styles.text} opacity-60 hover:opacity-100
            transition-opacity duration-200
          `}
        >
          <SvgClose className="text-sm" />
        </button>
      </div>
    </div>
  );
};

export default CustomToast;
